<template>
  <div class="grid grid-cols-1 md:grid-cols-2 bg-white font-sans font-size-reset">
    <div class="bg-gradient-to-r from-cal-blue via-cal-light-blue to-cal-green flex items-start justify-center">
      <plan-details
          :sticky="planDetailsSticky"
          :coverage-type="coverage_type"
          :coverage-limit="current_plan.coverage_limit"
          :plan-name="current_plan.name"
          :plan-price="pricing['Plan Price']"
          :plan-description="current_plan.description"
          :total-price="pricing['Total']"
          :users-count="current_plan.users_count"
          :is-free-trial="subscription.is_free_trial"
          :has-download="current_plan.download"
      >
      </plan-details>
    </div>
    <div class="bg-white p-4">
      <form class="md:ml-3 lg:ml-6 md:mr-7 lg:mr-24 bg-gray-200" @submit.prevent="subscribe">
        <form-section title="Account information" headline="Let's get started growing your business today!">
          <div class="space-y-2">
            <div class="">
              <label class="sr-only" for="company">Company</label>
              <input required type="text" v-model="subscription.company" id="company" placeholder="Company" class="block w-full sm:text-sm border border-gray-300 p-2"/>
            </div>
            <div class="flex flex-row gap-2">
              <div class="flex-grow">
                <label for="first_name" class="sr-only"><span>First name</span></label>
                <input required id="first_name" type="text" autocomplete="given-name" v-model="subscription.first_name"
                       placeholder="First name"  class="block w-full sm:text-sm border border-gray-300 p-2"/>
              </div>
              <div class="flex-grow ">
                <label for="last_name" class="sr-only"><span>Last name</span></label>
                <input required id="last_name" type="text" autocomplete="family-name" v-model="subscription.last_name"
                       placeholder="Last name"  class="block w-full sm:text-sm border border-gray-300 p-2"/>
              </div>
            </div>
            <div class="">
              <label class="sr-only" for="email">Email</label>
              <input required type="email" autocomplete="username" v-model="subscription.email" id="email" placeholder="Email"  class="block w-full sm:text-sm border border-gray-300 p-2"/>
            </div>
            <div class="">
              <label class="sr-only" for="phone">Phone</label>
              <input required type="tel" v-model="subscription.phone_number" id="phone" placeholder="Phone"  class="block w-full sm:text-sm border border-gray-300 p-2"/>
            </div>
            <div class="">
              <label class="sr-only" for="state">State</label>
              <div class="relative w-full">
                <select required id="state" v-model="subscription.state" class="block w-full appearance-none sm:text-sm border border-gray-300 pl-2 pr-8 py-2 mt-2">
                  <option value="" disabled>Select a state</option>
                  <option v-for="state in states" :value="state.abbr" :key="state.abbr">{{ state.name }}</option>
                </select>
              <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
  <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
</svg>
              </div>
              </div>

            </div>
            <div class="">
              <label class="sr-only" for="password">Password</label>
              <input required type="password" v-model="subscription.password" id="password" placeholder="Password" autocomplete="new-password"  class="block w-full sm:text-sm border border-gray-300 p-2"/>
            </div>
            <div class="">
              <label class="sr-only" for="confirm_password">Confirm password</label>
              <input required type="password" v-model="subscription.confirm_password" id="confirm_password"
                     placeholder="Confirm password" autocomplete="new-password"  class="block w-full sm:text-sm border border-gray-300 p-2"/>
            </div>
            <div v-if="subscription.confirm_password !== '' && subscription.password !== subscription.confirm_password">
              <p class="error-message">The passwords do not match</p>
            </div>
          </div>
        </form-section>
        <form-section title="Project types" headline="Time to customize your plan."
                      v-if="current_plan.coverage_vertical_market">
          <p v-if="current_plan.additional_vertical_market_cost > 0 && !subscription.is_free_trial">1 vertical included, $1,999.95 per additional
            vertical.</p>
          <div class="details__inner">
            <checkbox-cloud
                :options="verticals"
                v-model="subscription.plan_verticals"></checkbox-cloud>
          </div>
        </form-section>
        <form-section title="Coverage area" headline="Time to customize your plan." v-if="current_plan.coverage_state">
          <div class="space-y-2">
            <div v-for="n in coverage_limit" :key="n">
              <label :for="`state_${n}`" class="sr-only">State {{ n }}</label>
              <div class="relative w-full">
                <select v-model="subscription.plan_states[n - 1]" :disabled="subscription.plan_states[n - 2] === ''" required :name="`state_${n}`" :id="`state_${n}`" class="block w-full appearance-none sm:text-sm border border-gray-300 pl-2 pr-8 py-2 mt-2">
                  <option value="" disabled>Select a state</option>
                  <option v-for="state in states" :value="state.id" :disabled="subscription.plan_states.includes(state.id) || state === ''"  :key="state.abbr">{{state.name}}</option>
                </select>
              <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
  <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
</svg>
              </div>
              </div>
            </div>
          </div>
          <div v-if="current_plan.additional_state_cost > 0">
            <p class="text-sm my-2" v-if="!subscription.is_free_trial">1 state included, $835 per additional state</p>
            <button type="button" formnovalidate @click="addAdditionalState()" :class="{'mt-2': subscription.is_free_trial}" class="border-0 bg-cal-gold outline-none rounded-md text-cal-blue py-1 px-2 uppercase font-bold">Add additional state</button>
          </div>
        </form-section>
        <form-section title="Coverage counties" headline="Time to customize your plan." v-if="current_plan.coverage_county">
          <div class="space-y-2">
            <county-selection v-for="n in coverage_limit" :key="n"
                              :name="n.toString()"
                              :states="states"
                              v-model="subscription.plan_counties[n - 1]"
            ></county-selection>
          </div>
        </form-section>
        <form-section title="Payment" headline="You're almost in." v-if="!subscription.is_free_trial">
          <div class="space-y-3">
            <div class="flex flex-row gap-2">
              <div class="flex-grow">
                <label for="billing_first_name" class="sr-only"><span>Billing first name</span></label>
                <input required id="billing_first_name" type="text" autocomplete="given-name"
                       v-model="subscription.bill_to.first_name" placeholder="First name" class="block w-full sm:text-sm border border-gray-300 p-2"/>
              </div>
              <div class="flex-grow ">
                <label for="billing_last_name" class="sr-only"><span>Billing last name</span></label>
                <input required id="billing_last_name" type="text" autocomplete="family-name"
                       v-model="subscription.bill_to.last_name" placeholder="Last name" class="block w-full sm:text-sm border border-gray-300 p-2"/>
              </div>
            </div>
            <div class="">
              <label class="sr-only" for="address1">Address 1</label>
              <input required type="text" v-model="subscription.bill_to.address" id="address1" placeholder="Address 1" class="block w-full sm:text-sm border border-gray-300 p-2"/>
            </div>
            <div class="">
              <label class="sr-only" for="address2">Address 2</label>
              <input type="text" v-model="subscription.bill_to.address2" id="address2" placeholder="Address 2" class="block w-full sm:text-sm border border-gray-300 p-2"/>
            </div>
            <div class="">
              <label class="sr-only" for="billing_city">City</label>
              <input required type="text" v-model="subscription.bill_to.city" id="billing_city" placeholder="City" class="block w-full sm:text-sm border border-gray-300 p-2"/>
            </div>
            <div class="flex flex-row gap-2">
              <div class=" flex-grow">
                <label class="sr-only" for="billing_state">State</label>
                <div class="relative w-full">
                  <select  class="block w-full appearance-none sm:text-sm border border-gray-300 pl-2 pr-8 py-2" required id="billing_state" v-model="subscription.bill_to.state">
                    <option value="" disabled>Select a state</option>
                    <option v-for="state in states" :value="state.abbr" :key="state.abbr">{{ state.name }}</option>
                  </select>
              <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
  <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
</svg>
              </div>
                </div>
              </div>
              <div class="">
                <label class="sr-only" for="billing_zip">Zip</label>
                <input required type="text" v-model="subscription.bill_to.zip" id="billing_zip" placeholder="Zip code" class="block w-full sm:text-sm border border-gray-300 p-2"/>
              </div>
            </div>
            <hr class="border border-gray-300"/>
            <div class="">
              <label class="sr-only" for="billing_cc">Card number</label>
              <input required type="text" pattern="[0-9]*" inputmode="numeric" id="billing_cc" placeholder="Card Number"
                     v-model="subscription.card.card_number" autocomplete="cc-number" class="block w-full sm:text-sm border border-gray-300 p-2"/>
            </div>
            <div class="flex flex-row gap-2">
              <div class=" flex-grow">
                <label class="sr-only" for="billing_exp">Expiration (mm/yy)</label>
                <input required type="text" pattern="(?:0[1-9]|1[0-2])/[0-9]{2}" inputmode="numeric" id="billing_exp" placeholder="Expiration (mm/yy)"
                       v-model="expiration_date" autocomplete="cc-exp" class="block w-full sm:text-sm border border-gray-300 p-2"/>
              </div>
              <div class=" flex-grow">
                <label class="sr-only" for="billing_cvv">CVV</label>
                <input required type="text" pattern="[0-9]*" inputmode="numeric" id="billing_cvv" placeholder="CVV"
                       v-model="subscription.card.cvv" autocomplete="cc-csc" class="block w-full sm:text-sm border border-gray-300 p-2"/>
              </div>
            </div>
            <hr class="border border-gray-300"/>
            <div v-if="subscription.is_free_trial === false && allowPromoCodes === true">
              <label class="sr-only" for="promo_code">Promotional code</label>
              <input type="text" v-model="subscription.promo_code" id="promo_code" placeholder="Promo code" class="block w-full sm:text-sm border border-gray-300 p-2"/>
            </div>
          </div>
        </form-section>
        <input type="hidden" name="discount" v-model="subscription.discount"/>
        <div class="bg-white" v-if="error">
          <p class="bg-red-500 m-0 p-2 text-white text-sm font-bold">{{ errorMessage }}</p>
        </div>
        <input type="hidden" name="free_trial_source" v-if="subscription.is_free_trial" v-model="subscription.referrer" />
        <div class="bg-white py-4">
          <div class="text-center text-sm my-2">
            <label for="terms_of_service_agreement">
              <input v-model="agreedToTerms" type="checkbox" id="terms_of_service_agreement" required/>
              I have read and agree to the <a href="https://constructalead.com/terms-of-service/" target="_blank">terms
              of
              service</a>.
            </label>
          </div>
          <div class="text-center">
            <button :disabled="!agreedToTerms" :class="{ 'cursor-not-allowed opacity-70': !agreedToTerms }" class="bg-cal-gold px-6 py-2 text-cal-blue uppercase border-0 font-bold rounded-sm shadow-md">
              	<span v-if="subscription.is_free_trial">View projects</span>
              	<span v-else>Create account</span>
            </button>
          </div>
          <div v-if="!subscription.is_free_trial">
            <p class="text-xs my-2">When you make a purchase with us, the charge on your credit card statement will appear as &quot;constructalead.com&quot;. This descriptor helps you easily identify the transaction and ensures clarity in your financial records. Please also note charges are a one-time charge.</p>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>

import auth from '../../services/Client/Auth';
import FormSection from './SignUp/FormSection';
import VueButton from "../Shared/VueButton.vue";
import CheckboxCloud from "../Shared/CheckboxCloud.vue";
import Format from "../../mixins/Format";
import PlanDetails from "./SignUp/PlanDetails";
import CountySelection from "./SignUp/CountySelection.vue"

export default {
  mixins: [Format],
  components: {PlanDetails, VueButton, CheckboxCloud, FormSection, CountySelection},
  data() {
    const {query} = this.$route;
    if (query.fullname) {
      const splitName = query.fullname.split(' ')
      query.first_name = splitName.shift()
      query.last_name = splitName.join(' ');
    }
    const isFreeTrial = typeof query.free_trial !== 'undefined' && query.free_trial !== '' && query.free_trial !== null;
    return {
      currentBounds: null,
      planDetailsSticky: false,
      allowPromoCodes: true,
      pricingCheckTimeout: null,
      additional_services: [],
      additional_coverage_count: 0,
      current_plan: {
        base_price: 0,
        additional_vertical_market_cost: 0,
        additional_state_cost: 0,
        description: '',
        name: '',
        coverage_limit: 0,
        coverage_state: 1,
        users_count: 0,
        download: false,
      },
      counties: [],
      countiesCurrentState: '',
      states: [],
      provinces: [],
      verticals: [],
      pricing: {
        'Plan Price': '$',
        'Total': '$',
      },
      subscription: {
        first_name: query.first_name || '',
        last_name: query.last_name || '',
        phone_number: query.phone || '',
        email: query.email || '',
        company: query.company || '',
        state: '',
        password: '',
        confirm_password: '',
        referrer: this.getReferrer(query),
        card: {
          card_number: '',
          expiration_date: '',
          cvv: '',
        },
        bill_to: {
          first_name: query.first_name || '',
          last_name: query.last_name || '',
          address: '',
          address2: '',
          state: '',
          country: 'US',
          zip: '',
        },
        promo_code: null,
        is_free_trial: isFreeTrial,
        plan: null,
        plan_verticals: [],
        plan_states: [],
        plan_counties: [],
        additional_services: [],
        discount: query.discount || '',
      },
      error: false,
      errorMessage: '',
      agreedToTerms: false
    }
  },
  computed: {
    coverage_type() {
      if (this.current_plan.coverage_state === 1) {
        return 'state';
      }
      if (this.current_plan.coverage_county === 1) {
        return 'county';
      }
      if (this.current_plan.coverage_vertical_market === 1) {
        return 'vertical_market';
      }
      return '';
    },
    coverage_limit() {
      return this.current_plan.coverage_limit + this.additional_coverage_count;
    },
    pricingData() {
      return {
        promo_code: this.subscription.promo_code,
        plan: this.subscription.plan,
        plan_verticals: this.subscription.plan_verticals,
        plan_states: this.subscription.plan_states,
        plan_counties: this.subscription.plan_counties,
        additional_services: this.subscription.additional_services,
        is_free_trial: this.subscription.is_free_trial,
      }
    },
    expiration_date: {
      get() {
        return this.subscription.card.expiration_date;
      },
      set(value) {
        let newValue = value;
        newValue = newValue.replace(/\D/g, '');
        if ( newValue.match(/^\d{0,4}$/g) ) {
          newValue = this.mask(newValue, 2, '/');
          this.subscription.card.expiration_date = newValue;
        } else {
          this.subscription.card.expiration_date = value;
        }
      }
    }
  },
  watch: {
    'countiesCurrentState': function (value) {
      this.$http.get(`/Register/Counties/${value}`)
          .then(res => {
            this.counties = res.body.data;
          })
          .catch(err => {
            this.counties = [];
            this.error = 'Unknown error while fetching counties.'
          })
    },
    'subscription.state': function (value) {
      // If no states have been selected, or just one... set to current state selected.
      if (this.current_plan.coverage_state === 1) {
        if (this.subscription.plan_states.filter(val => val !== '').length < 2) {
          this.subscription.plan_states[0] = this.states.filter(state => state.abbr === value).map(state => state.id)[0];
        }
      }
      this.subscription.bill_to.state = value;
    },
    'subscription.first_name': function (value) {
      this.subscription.bill_to.first_name = value;
    },
    'subscription.last_name': function (value) {
      this.subscription.bill_to.last_name = value;
    },
    pricingData() {
      if (this.pricingCheckTimeout) {
        clearTimeout(this.pricingCheckTimeout)
      }
      this.pricingCheckTimeout = setTimeout(this.getPricing, 100);
    }
  },
  methods: {
    mask(value, limit, separator) {
      const output = [];
      for (let i = 0; i < value.length; i++) {
        if ( i !== 0 && i % limit === 0) {
          output.push(separator);
        }
        output.push(value[i]);
      }
      return output.join("");
    },
    handleScroll(event) {
      const self = this;
      clearTimeout(this.timeout)
      this.timeout = setTimeout(function () {
        const offset = window.pageYOffset;
        self.planDetailsSticky = offset >= self.currentBounds.top;
      }, 5)
    },
    addAdditionalState() {
      this.additional_coverage_count++;
      this.subscription.plan_states.push('')
    },
    countyValue(county) {
      return county.name + '|' + county.state_abbr
    },
    subscribe() {
      const {planId} = this.$route.params;

      if (this.subscription.password !== this.subscription.confirm_password) {
        this.error = true;
        this.errorMessage = 'Passwords do not match';
        return;
      }
      const router = this.$router;
      this.$http.post("/Register", this.subscription)
          .then(function (response) {
            if (typeof dataLayer !== 'undefined') {
              try {
                dataLayer.push({
                  event: 'subscription_signup',
                  attributes: this.pricingData
                })
              } catch (err) {
                console.error(err)
              }
            }
            // @todo the response _should_ be providing the user ID and token
            // We can skip the login and set the token directly.
            // auth.setToken(response.data.user.token)
            auth.login({
              email: this.subscription.email,
              password: this.subscription.password
            }, function (err, success) {
              router.push({
                name: 'signup.complete',
                params: {
                  planId
                }
              });
            }.bind(this));
          }.bind(this))
          .catch(function (err) {
            if (err.data) {
              this.error = true;
              this.errorMessage = err.data.message
            } else {
              this.error = true;
              this.errorMessage = 'Sorry, there was an unknown error. Call us at 855-874-1491'
            }
          }.bind(this));
    },
    getPricing() {
      if (this.pricingData.plan !== null) {
        this.$http.post("/Register/Price", this.pricingData)
            .then(function (response) {
              this.pricing = response.data.data.components
            }.bind(this));
      }
    },
    getReferrer(query) {
      if (query.utm_campaign) {
        return query.utm_campaign;
      }
      return ('; ' + document.cookie).split(`; signup_referrer=`).pop().split(';')[0];
    }
  },
  destroyed() {
    document.removeEventListener('scroll', this.handleScroll);
  },
  created() {
    this.$nextTick(() => {
      this.currentBounds = this.$el.getBoundingClientRect();
      document.addEventListener('scroll', this.handleScroll);
    })
    let self = this;
    this.$http.get("/Register/Options")
        .then(function (response) {
          self.additional_services = response.data.data.additional_services;
          self.states = response.data.data.states;
          self.provinces = response.data.data.provinces;
          self.verticals = response.data.data.verticals;

          const {planId} = self.$route.params;
          const filteredPlan = response.data.data.plans.filter(item => item.slug === planId || parseInt(item.id) === parseInt(planId))
          if (filteredPlan.length === 0) {
            window.location.href = 'https://constructalead.com/sign-up/';
          }

          // Populate some subscription details before we set it as the current plan.
          self.subscription.plan = filteredPlan[0].id
          if (filteredPlan[0].coverage_state === 1) {
            for (let i = 0; i < filteredPlan[0].coverage_limit; i++) {
              self.subscription.plan_states.push('')
            }
          }
          if (filteredPlan[0].coverage_county === 1) {
            for (let i = 0; i < filteredPlan[0].coverage_limit; i++) {
              self.subscription.plan_counties.push({
                state_abbr: '',
                name: '',
              })
            }
          }
          if (filteredPlan[0].additional_vertical_market_cost === 0) {
            self.subscription.plan_verticals = self.verticals.map(vertical => vertical.id)
          }

          self.current_plan = filteredPlan[0]

          if (self.current_plan.slug === 'state-edition' && self.subscription.is_free_trial) {
            self.current_plan.description = self.current_plan.description.replace('one U.S. state', 'one U.S state or Canadian province')
            self.states = [...self.states, '', ...self.provinces]
          }

          self.getPricing()
        }.bind(this));
  },
  route: {
    activate() {
      this.error = null;
    }
  }

}

</script>

<style scoped>
.font-size-reset {
  font-size: 16px;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}

.flex-grow input {
  width: 100%;
}

.project-types .checkbox-cloud {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.coverage-areas .checkbox-cloud {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

form.signup-form .checkbox-cloud span, .checkbox-cloud label {
  width: 100%;
}

.error-message {
  background: #fc8181;
  border-left: 4px solid #e53e3e;
  margin: 0;
  padding: 1em;
}
</style>

